import { batch, useSignal } from "@preact/signals";
import { rpc } from "../rpc";
import { ACTIVITY_PAGE_SIZE, type ActivityItem } from "../types";

type UseOrgActivityOptions = {
  orgId: string;
  initialActivity: ActivityItem[];
  initialHasMore: boolean;
};

/**
 * Hook for managing paginated org activity feed.
 */
export function useOrgActivity({
  orgId,
  initialActivity,
  initialHasMore,
}: UseOrgActivityOptions) {
  const activity = useSignal<ActivityItem[]>(initialActivity);
  const hasMore = useSignal(initialHasMore);
  const loadingMore = useSignal(false);
  const loadMoreError = useSignal<string | null>(null);

  async function loadMore() {
    if (loadingMore.value || !hasMore.value || !orgId) return;
    loadingMore.value = true;

    try {
      const result = await rpc.submissions.orgActivity({
        orgId,
        limit: ACTIVITY_PAGE_SIZE,
        offset: activity.value.length,
      });
      batch(() => {
        activity.value = [...activity.value, ...result.submissions];
        hasMore.value = result.hasMore;
        loadingMore.value = false;
        loadMoreError.value = null;
      });
    } catch (err) {
      batch(() => {
        loadMoreError.value =
          err instanceof Error ? err.message : "Failed to load activity";
        loadingMore.value = false;
      });
    }
  }

  return {
    activity,
    hasMore,
    loadingMore,
    loadMoreError,
    loadMore,
  };
}
